import { useState } from 'react';
import { Link } from 'react-router-dom';
import { match } from 'ts-pattern';

import { Loader } from '@/components/loader/loader';
import { useSecretDetailQuery } from '@/hooks/queries/useSecrets';
import { SecretListItem, SecretTypeEnum, SecretWithValue } from '@/types/secret';

import { ManageAccessModal } from './manageAccessModal';
import styles from './secretsPanel.module.scss';

type Props = {
  secret: SecretListItem;
  agentNameMap: Map<string, string>;
};

function getEnvVarName( detail: SecretWithValue ): string | null {
  if ( detail.globalEnvVarName ) return detail.globalEnvVarName;
  if ( detail.agentMappings?.length > 0 ) return detail.agentMappings[0].envVarName;
  return null;
}

export function ExpandedSecretRow( { secret, agentNameMap }: Props ) {
  const [ showManageAccessModal, setShowManageAccessModal ] = useState( false );
  const { data: detail, isLoading, isError } = useSecretDetailQuery( secret.id );

  if ( isLoading ) {
    return <Loader />;
  }

  if ( isError || !detail ) {
    return <div className={ styles.sub_row_content }>Failed to load secret details.</div>;
  }

  const isAgentType = detail.type === SecretTypeEnum.AGENT || detail.type === SecretTypeEnum.EVALS;
  const envVar = getEnvVarName( detail );
  const mappings = detail.agentMappings ?? [];
  const description = detail.description ?? '-';

  if ( isAgentType ) {
    return (
      <ul className={ styles.sub_row_content }>
        {envVar && (
          <li className={ styles.sub_row_field }>
            <strong>Environment Variable:</strong> { envVar }
          </li>
        )}
        <li className={ styles.sub_row_field }>
          <strong>Description:</strong> { description }
        </li>
        <li className={ styles.sub_row_field }>
          <strong>Constellations:</strong>
          {match( { global: detail.global, count: mappings.length } )
            .with( { global: true }, () => (
              <span>
                All constellations assigned &middot;{' '}
                <Link className={ styles.constellation_link } to="/agents">
                  View constellations
                </Link>
              </span>
            ) )
            .with( { count: 0 }, () => (
              <span className={ styles.warning_text }>
                No constellations assigned &middot;{' '}
                <span
                  className={ styles.constellation_link }
                  onClick={ () => setShowManageAccessModal( true ) }
                >
                  Manage access
                </span>
              </span>
            ) )
            .otherwise( () => (
              <span>{ mappings.length } constellation{ mappings.length !== 1 ? 's' : '' } assigned</span>
            ) )}
        </li>
        {!detail.global && mappings.length > 0 && (
          <li>
            <ul className={ styles.constellation_list }>
              {mappings.map( m => (
                <li key={ `${ m.agentId }-${ m.envVarName }` }>
                  { agentNameMap.get( m.agentId ) ?? m.agentId } &middot;{' '}
                  <Link
                    className={ styles.constellation_link }
                    to="/agents"
                    state={ { constellationFilter: agentNameMap.get( m.agentId ) ?? m.agentId } }
                  >
                    View constellation
                  </Link>
                </li>
              ) )}
            </ul>
          </li>
        )}
        {showManageAccessModal && (
          <ManageAccessModal
            isOpen={ showManageAccessModal }
            onClose={ () => setShowManageAccessModal( false ) }
            secret={ secret }
          />
        )}
      </ul>
    );
  }

  // DATA_PLATFORM secrets
  return (
    <ul className={ styles.sub_row_content }>
      <li className={ styles.sub_row_field }>
        <strong>Description:</strong> { description }
      </li>
    </ul>
  );
}
